import { get, writable, readable, type Readable, type Writable } from "svelte/store";
import getSessionStore, { getNotificationsStore, getSessionTemplateStore } from "../session-store";
import route from "../Util/routes";
import { getRequest, isUserInSession } from "../Util/utils";
import XertPusher from "../xert-pusher";
import type { CalendarSettings } from "@/Common/constants";
import { getActivityCommentCounts, getDayCommentCounts } from "@/Comments/api";

export type Session = {
    name: string,
    session_id: string,
}
export type Template = {
    name: string,
    template_id: string,
}
export interface IMyFitnessStores {
    trainingAdvice: Writable<any>
    trainingPlan: Writable<any>
    selectedTrainingPlan: Writable<any>
    trickleActive: Writable<boolean>
    recommendedTrainingUpdatedAt: Writable<Date>
    userParams: Writable<any>
    sessions: Readable<Session[]>
    sessionTemplates: Readable<Template[]>
}

export class MyFitnessStores implements IMyFitnessStores {
	trainingAdvice: Writable<any>
    trainingPlan: Writable<any>;
    trickleActive: Writable<boolean>
    selectedTrainingPlan: Writable<any>
    recommendedTrainingUpdatedAt: Writable<Date>
    trainingAdviceUpdatedAt: Writable<Date>
    userParams: Writable<any>
    sessions: Readable<Session[]>
    sessionTemplates: Readable<Template[]>
    sessionUpdates: Writable<Session>
    notifications: Readable<any>
    cal_settings: Writable<CalendarSettings>
    // {total, unread} comment counts per activity path -- populated on demand (see
    // loadCommentCounts) by whichever list/page owns a batch of activities (Today, calendar,
    // ActivitiesDash), then read by CommentsButton instances further down that same component
    // tree via getContext('myFitness'), instead of prop-drilling counts through every card layer.
    commentCounts: Writable<Record<string, { total: number; unread: number }>>
    // Same, for calendar-day comment threads, keyed by the 'YYYY-MM-DD' LocalDate.format() string
    // (see loadDayCommentCounts). Separate from commentCounts above because the two key spaces are
    // unrelated -- an activity path identifies a thread on its own, a day only does together with
    // the athlete whose calendar it is.
    dayCommentCounts: Writable<Record<string, { total: number; unread: number }>>
    // Days already asked for, so scrolling the (virtualized, constantly rebuilt) calendar re-requests
    // only the days that scrolled into view. Local edits keep this cache honest -- see
    // markDayCommentsRead/addDayComment.
    private requestedDays = new Set<string>()
    // Whose calendar dayCommentCounts is currently holding days for (see loadDayCommentCounts) --
    // a coach viewing an athlete is on their *own* Pusher channel, so pushed day comments have to
    // be matched against this before they touch a badge.
    private dayCountsSubject: string | null = null

    private loggedIn: Boolean
    private session_store_controller = {postSession: (s)=>{}, postSessionsDirectly: ()=>{}, postSessions: (ss)=>{}, onUpdate: (s: Session)=>{}}
    private session_template_store_controller = {postSessionTemplate: (s)=>{}, postSessionTemplates: (ss)=>{}}
    private pusher: XertPusher;

    constructor(up, ta, cal_settings, tp = null, should_load_session_list = false, loggedIn = false, trickle_is_active = false){
        this.trainingAdvice = writable(ta);
        this.trainingPlan = writable(tp);
        this.trickleActive = writable(trickle_is_active);
        this.recommendedTrainingUpdatedAt = writable(new Date());
        // When the advice was rendered server side it carries the time it was calculated, and
        // MyFitness compares that against the (server stamped) time on pushed refresh events.
        // Seeding this with the browser clock instead makes any device running ahead of the
        // server discard every refresh for the whole session, so when the timestamp is missing
        // start at the epoch and let the first event through — at worst that costs one refetch.
        this.trainingAdviceUpdatedAt = writable(ta?.timestamp ? new Date(ta.timestamp) : new Date(0));
        this.userParams = writable(up);
        this.loggedIn = loggedIn;
        this.sessionUpdates = writable();
        this.selectedTrainingPlan = writable();
        this.cal_settings = writable(cal_settings);
        this.commentCounts = writable({});
        this.dayCommentCounts = writable({});

        // If no userParams given. Fetch.
        if (!up) this.updateUserParams()
  

        if (loggedIn) {
            // Initialize Pusher.
            this.pusher = new XertPusher();
            this.pusher.init();
        }
        this.notifications = loggedIn ? getNotificationsStore(this.pusher, this.userParams) : readable([], ()=>{});

        this.pusher?.getUserChannel()?.bind('comment', this.onCommentPushed);

        // Init sessions store.
        let loading_sessions = false;
        let error_loading_sessions = false;
        this.session_store_controller.onUpdate = (s) => {
            this.sessionUpdates.set(s);
        }
        this.sessions = (should_load_session_list && loggedIn) ? getSessionStore(this.pusher,
            this.userParams, this.getSession,
            this.session_store_controller)
            : readable([], ()=>{});

        let loading_session_templates = true;
        this.sessionTemplates = (should_load_session_list && loggedIn) ? getSessionTemplateStore(this.pusher,
            this.userParams, this.getSessionTemplate,
            this.session_template_store_controller)
            : readable([], ()=>{});
    }
    close() {
        this.pusher?.getUserChannel()?.unbind('comment', this.onCommentPushed);
        this.pusher.close();
    }

    // Batch-fetches {total, unread} comment counts for the given activity paths and merges them
    // into commentCounts. Fetched once per call (no live/Pusher updates) -- callers re-invoke this
    // whenever their own activity list reloads (new page, new calendar range, etc.). Chunked at
    // 100 paths per request to match the backend's validation cap.
    async loadCommentCounts(paths: string[]) {
        //@ts-ignore
        if (!window.Xert?._hasMessagingFeature) return;
        const uniquePaths = [...new Set(paths)].filter(Boolean);
        if (uniquePaths.length === 0) return;
        const chunkSize = 100;
        try {
            for (let i = 0; i < uniquePaths.length; i += chunkSize) {
                const counts = await getActivityCommentCounts(uniquePaths.slice(i, i + chunkSize));
                this.commentCounts.update((prev) => ({ ...prev, ...counts }));
            }
        } catch (e) {
            console.log(e);
        }
    }

    // Zeroes just the unread half of one path's entry, leaving `total` alone -- called by
    // CommentsButton/ExerciseDialog the moment that activity's thread actually gets marked read,
    // so every badge reading this path (it may be shown on more than one screen at once, e.g.
    // Today and a list view) updates immediately instead of waiting on the next
    // loadCommentCounts() batch reload.
    markCommentsRead(path: string) {
        this.commentCounts.update((prev) => {
            const existing = prev[path];
            if (!existing || existing.unread === 0) return prev;
            return { ...prev, [path]: { ...existing, unread: 0 } };
        });
    }

    // Day-thread twin of loadCommentCounts, for the calendar's per-day badges. $forUser is the
    // athlete's username when a coach is viewing their calendar (omitted when the athlete views
    // their own) -- same convention the notes endpoints use. Days already fetched are skipped, so
    // the caller can hand this every day currently rendered on each (frequent) row rebuild and only
    // newly scrolled-in days cost a request. Days with no thread are recorded as zeroes rather than
    // left absent, so they aren't re-requested on the next rebuild.
    async loadDayCommentCounts(days: string[], forUser?: string) {
        //@ts-ignore
        if (!window.Xert?._hasMessagingFeature) return;
        this.dayCountsSubject = forUser ?? null;
        const newDays = [...new Set(days)].filter((d) => d && !this.requestedDays.has(d));
        if (newDays.length === 0) return;
        newDays.forEach((d) => this.requestedDays.add(d));
        const chunkSize = 100;
        try {
            for (let i = 0; i < newDays.length; i += chunkSize) {
                const chunk = newDays.slice(i, i + chunkSize);
                const counts = await getDayCommentCounts(chunk, forUser);
                this.dayCommentCounts.update((prev) => {
                    const next = { ...prev };
                    for (const day of chunk) next[day] = counts[day] ?? { total: 0, unread: 0 };
                    return next;
                });
            }
        } catch (e) {
            // Let a failed range be retried on the next rebuild rather than caching the failure.
            newDays.forEach((d) => this.requestedDays.delete(d));
            console.log(e);
        }
    }

    markDayCommentsRead(day: string) {
        this.dayCommentCounts.update((prev) => {
            const existing = prev[day];
            if (!existing || existing.unread === 0) return prev;
            return { ...prev, [day]: { ...existing, unread: 0 } };
        });
    }

    // Keeps a day's badge in step with a comment just posted from this client (nothing refetches
    // these counts while the calendar stays put). The new comment is the poster's own, so it counts
    // toward `total` only -- never unread.
    addDayComment(day: string) {
        this.bumpCommentCount(this.dayCommentCounts, day, false);
    }

    // Live comments from other people, so badges climb while the page sits open instead of only at
    // the next load. Only a thread's *other* participants are pushed this event, so anything
    // arriving here is someone else's comment: unread as well as total. If that thread happens to
    // be open on screen, CommentThreadPanel marks it read on arrival and its onRead zeroes the
    // unread half right back (it binds this same event after this handler, so the order holds).
    // Threads for a day/activity that was never loaded get an entry created here -- harmless, and
    // overwritten by the server's count if that range is loaded later.
    private onCommentPushed = (event: { thread_type?: string; activity_path?: string | null; day?: string | null; subject_username?: string | null }) => {
        if (event.thread_type === 'activity' && event.activity_path) {
            this.bumpCommentCount(this.commentCounts, event.activity_path, true);
        } else if (event.thread_type === 'day' && event.day && event.subject_username === this.dayCountsSubject) {
            this.bumpCommentCount(this.dayCommentCounts, event.day, true);
        }
    }

    private bumpCommentCount(store: Writable<Record<string, { total: number; unread: number }>>, key: string, unread: boolean) {
        store.update((prev) => {
            const existing = prev[key] ?? { total: 0, unread: 0 };
            return { ...prev, [key]: { total: existing.total + 1, unread: existing.unread + (unread ? 1 : 0) } };
        });
    }

    getTemplatesPromise() {
        return this.session_template_store_controller.initial;
    }

    getSession(path: string) {
        let promise = window.fetch(route('SessionsController@getSession', [path]), {
            ...getRequest(),
            method: 'get',
        })
        .then(response => response.ok ? response.json() : Promise.reject(`getSession error ${response.status}`));

        return promise.catch(error=>{
            console.log('getSession error!');
            console.log(error);
        });
    }

    getSessionTemplate(path: string) {
        console.log('getSessionTemplate called!');
        let promise = window.fetch(route('SessionsController@getSessionTemplate', [path]), {
            ...getRequest(),
            method: 'get',
        })
        .then(response => response.json());

        return promise.catch(error=>{
            console.log('getSessionTemplate error!');
            console.log(error);
        });
    }

    postSessions(data) {
        this.session_store_controller.postSessions(data);
    }
    updateSessions(data) {
        this.session_store_controller.postSessionsDirectly(data);
    }
    postSessionTemplates(data) {
        this.session_template_store_controller.postSessionTemplates(data);
    }


    updateUserParams() {
        if (this.loggedIn) return window.fetch(route('UserController@getUser'), {
            ...getRequest(),
            method: 'get',
        })
        .then(response => response.json())
        .then(data => {
            this.userParams.update((_)=>data)
        }).catch(error=>{
            console.log(error)
        });
        else return Promise.reject();
    }

    // Arrow function used here otherwise 'this' is undefined.
    joinSessionById = async (session_id: string, force: boolean) => {
        // If session id not in list, get session details from server.
        let s = this.findSession(session_id) ?? await this.getSession(session_id);
        return this.joinOrLeaveSession(s, force);
    }

    findSession(session_id: string) {
        return get(this.sessions).find(s=>s.session_id == session_id);
    }

    findSessionTemplate(template_id: string) {
        return get(this.sessionTemplates).find(s=>s.template_id == template_id);
    }

    // TODO: Determine if refactoring is necessary here.
    joinOrLeaveSession(session: Session, force: boolean) {
        console.log(`joinOrLeaveSession called! ${session.session_id}`);

        let do_join = !isUserInSession(session);
        let url = do_join ? route('SessionsController@postJoinSession', [session.session_id]) : route('SessionsController@postLeaveSession', [session.session_id])
        return window.fetch(url, {
            ...getRequest(),
            method: 'post',
            body: JSON.stringify({resolve_conflicts: force ?? false}),
        })
        .then(data=>data.json())
        .then(data=>{
            if (data.error) {
                throw data;
            } else {
                if (data?.session_id != null) {
                    this.session_store_controller.postSession(data);
                }
                return data;
            }
        });
    }
}

export class MedianAndWeatherStores {
    trainingMedians: Writable<any>
    weather: Writable<any>
    locationPermission: Writable<boolean>

    constructor(medians: any, weather: any = null, locationPermission = false) {        
        this.trainingMedians = writable(medians)
        this.weather = writable(weather)
        this.locationPermission = writable(locationPermission)
    }
}